import { Money } from "@frontend/components/ui";
import type { OfferChange } from "@creators/backend/lib/collabs/diff";
import type { OfferView } from "@creators/backend/lib/collabs/queries";
import { TRIGGER_LABELS } from "@creators/backend/lib/collabs/schedule";
import type {
  CollabStatus,
  MilestoneTrigger,
  Party,
} from "@creators/backend/lib/collabs/state-machine";
import { splitProportionally } from "@creators/backend/lib/money";
import { formatDate } from "@creators/backend/lib/time";

function milestoneDone(
  trigger: MilestoneTrigger,
  milestones?: { trigger: string; status: string }[],
): boolean {
  const row = milestones?.find((m) => m.trigger === trigger);
  // Only confirmed receipt counts as done — "paid" still needs the creator to confirm.
  return row?.status === "confirmed";
}

export function OfferCard({
  offer,
  changes,
  frozen,
  milestones,
}: {
  offer: OfferView;
  changes: OfferChange[];
  status: CollabStatus;
  pendingParty?: Party;
  viewer?: "creator" | "brand" | "admin";
  frozen: boolean;
  compact?: boolean;
  /** When present, settled stages show a tick instead of the step number. */
  milestones?: { trigger: string; status: string }[];
}) {
  const isBarter = offer.compensationType === "barter";
  const amounts = isBarter
    ? []
    : splitProportionally(
        offer.amountTotal,
        offer.schedule.map((s) => s.pct),
      );

  return (
    <section className="card overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-2.5">
        <h2 className="text-sm font-semibold text-ink">{frozen ? "Agreement" : "Offer"}</h2>
        <div className="flex shrink-0 items-center gap-1.5">
          {offer.version > 1 && !frozen ? <Tag>Updated</Tag> : null}
          <Tag>{frozen ? "Locked" : "Open"}</Tag>
        </div>
      </div>

      <div
        className={
          !isBarter && offer.schedule.length > 0 ? "px-4 pt-3" : "px-4 py-3"
        }
      >
        {isBarter ? (
          <p className="text-base leading-snug font-semibold">{offer.barterDescription}</p>
        ) : (
          <Money paise={offer.amountTotal} size="xl" className="text-xl tracking-tight" />
        )}

        <dl className="mt-3 space-y-1.5 border-t border-line pt-3 text-sm">
          <Row label="Deliver by">{formatDate(offer.deliverBy)}</Row>
        </dl>

        {!isBarter && offer.schedule.length > 0 ? (
          <div className="mt-3 border-t border-line pt-3">
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <p className="text-[11px] font-semibold tracking-wide text-ink-subtle uppercase">
                Payment plan
              </p>
              <p className="tabular text-xs text-ink-subtle">
                {offer.schedule.length} stage{offer.schedule.length === 1 ? "" : "s"}
              </p>
            </div>

            <div className="-mx-4 border-t border-line bg-canvas px-4 pt-3 pb-0">
              <ol className="space-y-0">
                {offer.schedule.map((item, index) => {
                  const last = index === offer.schedule.length - 1;
                  const done = milestoneDone(item.trigger, milestones);
                  return (
                    <li key={item.trigger} className="flex gap-3">
                      <div className="flex w-5 shrink-0 flex-col items-center">
                        <span
                          className={
                            done
                              ? "flex size-5 items-center justify-center rounded-full bg-money-600 text-canvas"
                              : "flex size-5 items-center justify-center rounded-full bg-surface text-[10px] font-bold text-ink-muted ring-1 ring-line-strong tabular"
                          }
                          aria-label={done ? "Paid" : `Stage ${index + 1}`}
                        >
                          {done ? (
                            <svg viewBox="0 0 16 16" fill="none" className="size-3" aria-hidden="true">
                              <path
                                d="M3.5 8.2 6.6 11.2 12.5 4.8"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          ) : (
                            index + 1
                          )}
                        </span>
                        {!last ? (
                          <span
                            className={
                              done ? "my-1 w-px flex-1 bg-money-600/40" : "my-1 w-px flex-1 bg-line"
                            }
                            aria-hidden="true"
                          />
                        ) : null}
                      </div>
                      <div className="flex min-w-0 flex-1 items-start justify-between gap-3 pb-3.5">
                        <span
                          className={
                            done
                              ? "pt-0.5 text-sm font-medium tracking-tight text-ink-muted"
                              : "pt-0.5 text-sm font-medium tracking-tight text-ink"
                          }
                        >
                          {TRIGGER_LABELS[item.trigger]}
                        </span>
                        <span className="shrink-0 text-right">
                          <span
                            className={
                              done
                                ? "block text-sm font-semibold tracking-tight text-ink-muted tabular"
                                : "block text-sm font-semibold tracking-tight text-ink tabular"
                            }
                          >
                            <Money paise={amounts[index]} size="sm" />
                          </span>
                          <span className="block text-[11px] font-semibold text-ink-subtle tabular">
                            {item.pct}%
                          </span>
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded bg-canvas px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-ink-subtle uppercase">
      {children}
    </span>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-ink-muted">{label}</dt>
      <dd className="tabular text-right font-medium text-ink">{children}</dd>
    </div>
  );
}
